import React from 'react';
import PropTypes from 'prop-types';

import { values as objectValues } from 'lodash';
import displayName from 'component-lib/displayName';
import { FormattedHTMLMessage } from 'component-utilities/intl';

import {
    FormContainer,
    FormItemLarge,
    FormRow,
    FormItemSmall,
    FormLabel,
    FormTitle,
    DropdownSmall as Dropdown,
} from 'v-c/Forms';
import ToggleSwitchWithTextAndLabel from 'v-c/Forms/CompositeComponents/ToggleSwitchWithTextAndLabel';

import SoftLimitTextAreaWithLabel from 'v-c/Forms/SoftLimitTextAreaWithLabel';
import { PrimaryButton, SecondaryButton, Spacer } from 'v-c/Buttons/Components';
import { Container, ContainerGroup } from 'v-c/Forms/InputGroup';
import LoadingIndicator from 'v-c/LoadingIndicator';
import MetadataTable from 'v-c/Footers/MetadataTable';
import { LargeCheckbox } from 'v-c/Forms/Checkboxes';

import SummaryForm from 'v-c/Reports/report_types/SummaryForm';

import { FIELDS } from 'v-c/Reports/config';

import Row from 'v-c/CompositeComponents/Forms/Row';
import FormDivider from 'v-c/Forms/FormDivider';
import styles from 'v-c/Reports/ReportEditForm.scss';

/* eslint-disable camelcase */
export default function ReportEditForm(props) {
    const {
        report: { report },
        report_id,
        fields: { name, description, enabled, MIN_HEIGHT },
        values,
        handleSubmit,
        isSaveDisabled,
        dirty,
        invalid,
        isUpdating,

        submitForm,
        cancelForm,
        getMessage,
        onToggle,
        emailAddress,
        frequency,
        onSelect,
        DELIVERY_OPTIONS,
        DROPDOWN_DELIVERY_ID,
    } = props;
    /* eslint-enable camelcase */

    const getLimit = which => FIELDS.find(({ NAME }) => NAME === which).LIMIT;

    const getKeyedMessage = (key, ...args) => getMessage(`components.reports.edit.${key}`, ...args);

    const dropdownData = () => DELIVERY_OPTIONS.map(id => ({ id, value: getKeyedMessage(`dropdown.${id}`) }));

    const buttonsProps = {
        getMessage,
        disabled: isSaveDisabled,
        onSubmit: handleSubmit(submitForm),
        onCancel: cancelForm,
    };

    const formLoaded = () => objectValues(values).some(value => typeof value !== 'undefined');

    const loaded = report && String(report.id) === String(report_id) && formLoaded();

    if (!loaded) {
        return (
            <div style={{ width: '100%', minHeight: '60px', position: 'relative' }}>
                <LoadingIndicator isLoading orientation="middle" />
            </div>
        );
    }

    return (
        <FormContainer>
            <FormTitle>
                {getKeyedMessage('header.basic_information')}
            </FormTitle>
            <FormRow>
                <FormItemLarge>
                    <SoftLimitTextAreaWithLabel
                        name="name"
                        label={getKeyedMessage('name.label')}
                        required={false}
                        inputStyle={styles['soft-limit-input-style']}
                        onChange={name.onChange}
                        minHeight={MIN_HEIGHT}
                        value={report.name || ''}
                        disabled
                        limit={getLimit('name')}
                        key="name"
                    />
                </FormItemLarge>
            </FormRow>
            <FormRow>
                <FormItemLarge>
                    <SoftLimitTextAreaWithLabel
                        name="description"
                        disabled
                        label={getKeyedMessage('description.label')}
                        required={false}
                        inputStyle={styles['soft-limit-input-style']}
                        onChange={description.onChange}
                        minHeight={MIN_HEIGHT}
                        value={report.description || ''}
                        limit={getLimit('description')}
                        key="description"
                    />
                </FormItemLarge>
            </FormRow>

            {report.type === 'summary' && <SummaryForm {...props} />}

            <FormTitle>
                {getKeyedMessage('header.option')}
            </FormTitle>
            <FormRow>
                <ToggleSwitchWithTextAndLabel
                    label={getKeyedMessage('status')}
                    enabled={enabled.checked}
                    onToggle={onToggle}
                    enabledText={getKeyedMessage('button_status.enabled')}
                    disabledText={getKeyedMessage('button_status.disabled')}
                />
            </FormRow>

            <FormTitle>
                {getKeyedMessage('header.delivery_options')}
            </FormTitle>
            <FormItemLarge>
                <FormLabel>
                    {getKeyedMessage('method')}
                </FormLabel>
                <LargeCheckbox disabled data-input="send-by-email" checked={values.sendByEmail}>
                    <FormattedHTMLMessage
                        id="components.reports.edit.via_email"
                        values={{
                            emailAddressClass: styles['email-address'],
                            emailAddress,
                        }}
                    />
                </LargeCheckbox>
            </FormItemLarge>

            <FormRow>
                <FormItemSmall>
                    <FormLabel>
                        {getKeyedMessage('frequency')}
                    </FormLabel>
                    <Dropdown
                        id={DROPDOWN_DELIVERY_ID}
                        data={dropdownData(getKeyedMessage)}
                        selectedId={frequency || ''}
                        onSelect={onSelect}
                        disabled={!values.sendByEmail}
                        placeholder={getKeyedMessage('dropdown.placeholder')}
                    />
                </FormItemSmall>
            </FormRow>
            <FormRow>
                <FormItemSmall>
                    <PrimaryButton
                        onClick={buttonsProps.onSubmit}
                        disabled={!dirty || invalid}
                        data-action="save"
                        loading={isUpdating}
                    >
                        {getKeyedMessage('buttons.save')}
                    </PrimaryButton>
                    <Spacer />
                    <SecondaryButton onClick={buttonsProps.onCancel} data-action="cancel">
                        {getKeyedMessage('buttons.cancel')}
                    </SecondaryButton>
                </FormItemSmall>
            </FormRow>

            <FormDivider />

            <ContainerGroup>
                <Container>
                    <Row>
                        <MetadataTable
                            item={report}
                            fields={['createdAt', 'updatedAt']}
                            baseKey="components.reports.edit.metadata"
                            getMessage={getMessage}
                        />
                    </Row>
                </Container>
            </ContainerGroup>
        </FormContainer>
    );
}

ReportEditForm.displayName = displayName('Reports/ReportEditForm');

ReportEditForm.propTypes = {
    report: PropTypes.object.isRequired,
    report_id: PropTypes.any.isRequired,
    fields: PropTypes.any.isRequired,
    values: PropTypes.any.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    isSaveDisabled: PropTypes.bool,
    dirty: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    isUpdating: PropTypes.bool.isRequired,

    submitForm: PropTypes.func.isRequired,
    cancelForm: PropTypes.func.isRequired,
    getMessage: PropTypes.func.isRequired,
    getKeyedMessage: PropTypes.func.isRequired,
    shouldDisable: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    emailAddress: PropTypes.string.isRequired,
    frequency: PropTypes.any.isRequired,
    onSelect: PropTypes.func.isRequired,
    reportTypes: PropTypes.array,
    DELIVERY_OPTIONS: PropTypes.any.isRequired,
    DROPDOWN_DELIVERY_ID: PropTypes.any.isRequired,
};

ReportEditForm.defaultProps = {
    isSaveDisabled: false,
    reportTypes: [],
};
